import { animate, state, style, transition, trigger } from '@angular/animations';
import { DatePipe, SlicePipe } from '@angular/common';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { MatSort } from '@angular/material/sort';
import { of } from 'rxjs/internal/observable/of';
import { mergeAll } from 'rxjs/internal/operators/mergeAll';
import { Subscription } from 'rxjs/internal/Subscription';
import { tap } from 'rxjs/operators';
import { IBreadcrumbData } from '../../../../interfaces/breadcrumb-data.interface';
import { SimpleDialogData } from '../../../../interfaces/simple-dialog-data.interface';
import { NotificationService } from '../../../../services/notification.service';
import { SimpleDialogService } from '../../../../services/simple-dialog.service';
import { WebSocketService } from '../../../../services/web-socket.service';
import { MovementDataSource } from '../../../movements/data-sources/movement.data-source';
import { MovementsService } from '../../../movements/services/movements.service';
import { EditComponent } from '../../components/edit/edit.component';
import { VisitVehicleDataSource } from '../../data-sources/visit-vehicle.data-source';
import { VisitVehicle } from '../../models/visit-vehicle.entity';
import { VisitVehicleService } from '../../services/visit-vehicle.service';

class VisitSituationNames {
  static readonly status = {
    0: 'Prevista',
    1: 'Registrado',
    2: 'Dentro',
    3: 'Fuera',
  };
}

class MovementsNames {
  static readonly result = {
    0: 'OK',
    1: 'Error lectura matrícula',
    2: 'No encontrado',
    3: 'No autorizado',
    7: 'Error indefinido',
    14: 'Antipassback',
    21: 'Fuera de horario',
    22: 'Sin estancia presente',
    23: 'Límite de vehículos',
    60: 'Matrícula no coincide',
    61: 'Carga especial',
    62: 'Overload',
    63: 'Underload',
    64: 'Verificar carga',
    99: 'No entrado',
  };

  static readonly opening = {
    0: 'No entrado',
    1: 'Automática',
    2: 'Manual',
    5: 'Validación externa',
    6: 'Freeflow',
  };
}

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'list',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class ListComponent implements OnInit, AfterViewInit {
  /**
   * Displayed columns array
   */
  displayedColumns: string[] = [
    'icon',
    'visitId',
    'plate',
    'initialEntryDate',
    'entryDate',
    'exitDate',
    'finalEntryDate',
    'timePermitVisit',
    'situation',
    'description',
    'visitor',
    'visited',
    'action',
  ];

  /**
   * Table config array
   */
  tableConfig: any[] = [
    {
      columnName: '',
    },
    {
      columnName: 'Visita',
    },
    {
      columnName: 'Matricula',
      pipe: new SlicePipe(),
      configPipe: [0, 50],
    },
    {
      columnName: 'Fecha Llegada',
      pipe: new DatePipe('es'),
      configPipe: ['d MMM y, H:mm'],
    },
    {
      columnName: 'Fecha Entrada',
      pipe: new DatePipe('es'),
      configPipe: ['d MMM y, H:mm'],
    },
    {
      columnName: 'Fecha Salida',
      pipe: new DatePipe('es'),
      configPipe: ['d MMM y, H:mm'],
    },
    {
      columnName: 'F. Límite Salida',
      pipe: new DatePipe('es'),
      configPipe: ['d MMM y, H:mm'],
    },
    {
      columnName: 'Permiso',
      pipe: (value) => {
        return value ? Math.floor(value / 60) + 'h ' + (value % 60) + 'm' : '';
      },
    },
    {
      columnName: 'Estado',
      pipe: (value) => {
        return VisitSituationNames.status[value] || value;
      },
    },
    {
      columnName: 'Descripcion',
    },
    {
      columnName: 'Visitante',
    },
    {
      columnName: 'Visitado',
    },
    '',
  ];

  /**
   * VisitVehicle data source
   */
  visitVehicleDataSource: VisitVehicleDataSource;

  /**
   * Movements displayed columns
   */
  movementsDisplayedColumns: string[] = [
    'icon',
    'movementId',
    'createdAt',
    'unifiedName',
    // 'clientName',
    // 'visitor',
    // 'owner',
    'door',
    'direction',
    'carPlateAssigned',
    'cardAssigned',
    'result',
    'opening',
  ];

  /**
   * Movements tableConfig
   */
  movementsTableConfig: any[] = [
    {
      columnName: '',
    },
    {
      columnName: 'id',
      enableSort: true,
    },
    {
      columnName: 'Fecha',
      pipe: new DatePipe('es'),
      configPipe: ['d MMM y, H:mm'],
      enableSort: true,
    },
    {
      columnName: 'Cliente/Visita/Propietario',
    },
    {
      columnName: 'Puerta',
    },
    {
      columnName: 'Direccion',
    },
    {
      columnName: 'Matrícula asignada',
    },
    {
      columnName: 'Tarjeta asignada',
    },
    {
      columnName: 'Resultado',
      pipe: (value: any) => {
        return MovementsNames.result[value] || value;
      },
    },
    {
      columnName: 'Apertura',
      pipe: (value: any) => {
        return MovementsNames.opening[value] || value;
      },
    },
  ];

  /**
   * Movements data source
   */
  movementsDataSource: MovementDataSource;

  /**
   * Paginator
   */
  @ViewChild(MatPaginator) paginator: MatPaginator;

  /**
   * Sort
   */
  @ViewChild(MatSort) sort: MatSort;

  /**
   * Filter object
   */
  filter: any;

  /**
   * Count of total visit vehicles
   */
  totalVisitVehicles: number;

  /**
   * BreadcrumbData
   */
  breadcrumbData: IBreadcrumbData;

  /**
   * Filter form
   */
  filterForm = new FormGroup({
    /**
     * visitId
     */
    visitId: new FormControl(),

    /**
     * plate
     */
    plate: new FormControl(),

    /**
     * situation
     */
    situation: new FormControl(),

    /**
     * today filter
     */
    todayFilter: new FormControl(),

    /**
     * date filter range
     */

    dateFilter: new FormGroup({
      start: new FormControl(null),
      end: new FormControl(null),
    }),
    /**
     * date filter
     */
    //dateFilter: new FormControl(null)
  });

  names = VisitSituationNames;

  private syncVisitVehiclesListSubscription: Subscription;
  private filterValueChangeSubscription: Subscription;
  private visitVehiclesCountSubscription: Subscription;
  private sortChangeSubscription: Subscription;
  public syncronized = true;
  public syncroAction: any;
  public expandedElement: any;
  /**
   * Constructor
   *
   * @param visitVehicleService VisitVehicleService
   * @param dialog MatDialog
   * @param simpleDialogService SimpleDialogService
   */
  constructor(
    private visitVehicleService: VisitVehicleService,
    public dialog: MatDialog,
    private simpleDialogService: SimpleDialogService,
    private snackBar: NotificationService,
    private wsService: WebSocketService,
    private movementsService: MovementsService
  ) {
    this.filter = {};

    this.breadcrumbData = {
      titles: {
        icon: 'directions_car',
        links: [
          {
            text: 'Lista Visitas',
          },
        ],
      },
      description: 'Aquí puede ver todas las visitas y puedes filtrarlas',
      buttons: [
        {
          text: 'Crear',
          action: () => this.create(),
          icon: 'add',
          disabled: false
        },
      ],
    };

    this.syncroAction = () => this.sync();
    this.socketSincro();
  }

  /**
   * ngOnInit
   */
  ngOnInit(): void {
    this.initTodayFilter();
    this.visitVehicleDataSource = new VisitVehicleDataSource(this.visitVehicleService);
    this.movementsDataSource = new MovementDataSource(this.movementsService);
    this.visitVehicleDataSource.load();
    // tslint:disable-next-line:max-line-length
    this.visitVehiclesCountSubscription = this.visitVehicleService.getCount().subscribe((data: any) => (this.totalVisitVehicles = data.count));

    this.filterForm.valueChanges.subscribe((value) => {
      if (value.dateFilter != null && value.dateFilter.start != null && value.dateFilter.end != null) {
        //this.filterForm.controls['todayFilter'].setValue(false);
        //this.filter.today = false;
        //this.filter.dateFilter = value.dateFilter.utc().toISOString();
        this.filter.dateFilter_from = value.dateFilter.start.utc().startOf('day').toISOString();
        this.filter.dateFilter_to = value.dateFilter.end.utc().endOf('day').toISOString();
      }
      if (value.todayFilter === true) {
        this.filter.today = true;
        //this.filterForm.controls['todayFilter'].setValue(null);
        //this.filter.dateFilter = null;
      } else {
        this.filter.today = false;
      }

      if (value.visitId != null) {
        this.filter.visitId = value.visitId;
      }
      if (value.plate != null) {
        this.filter.plate = value.plate;
      }
      if (value.situation != null) {
        this.filter.situation = value.situation;
      }

      this.loadVisitVehicles();
    });
  }

  /**
   * ngAfterViewInit
   */
  ngAfterViewInit(): void {
    this.sortChangeSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    of(this.sort.sortChange, this.paginator.page)
      .pipe(
        mergeAll(),
        tap(() => this.loadVisitVehicles())
      )
      .subscribe();
  }

  OnDestroy() {
    this.syncVisitVehiclesListSubscription.unsubscribe();
    this.sortChangeSubscription.unsubscribe();
    this.filterValueChangeSubscription.unsubscribe();
    this.visitVehiclesCountSubscription.unsubscribe();
  }

  /**
   * Sync
   */

  sync() {
    this.syncronized = !this.syncronized;
    if (this.syncronized) {
      this.loadVisitVehicles();
      this.socketSincro();
    } else {
      this.syncVisitVehiclesListSubscription.unsubscribe();
    }
  }

  /**
   * Socket Sincro
   */

  socketSincro() {
    this.syncVisitVehiclesListSubscription = this.wsService.listen('syncVisitVehiclesList').subscribe(
      (data) => {
        this.loadVisitVehicles();
      },
      (err) => {
        this.snackBar.showError(err);
      }
    );
  }

  /**
   * Create
   */
  create(): void {
    const dialogRef = this.dialog.open(EditComponent, { width: '500px', disableClose: true });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadVisitVehicles();
      }
    });
  }

  /**
   * Edit
   *
   * @param visitVehicle VisitVehicle instance
   */
  edit(visitVehicle: VisitVehicle): void {
    const dialogRef = this.dialog.open(EditComponent, {
      width: '500px',
      disableClose: true,
      data: {
        visitVehicleId: visitVehicle.visitId,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadVisitVehicles();
      }
    });
  }

  /**
   * ChangeState
   *
   * @param visitVehicle
   */

  ChangeSituation(visitVehicle: VisitVehicle): void {
    const data: SimpleDialogData = {
      title: 'Warning!',
      message: `Mover visita ${visitVehicle.visitId} fuera del recinto?`,
      rightButtonText: 'Yes',
    };

    this.simpleDialogService.open(data).subscribe((response) => {
      if (response) {
        visitVehicle.situation = 3;
        visitVehicle.lastExit = new Date();
        this.visitVehicleService.update(visitVehicle).subscribe(() => this.loadVisitVehicles());
      }
    });
  }

  /**
   * Remove
   *
   * @param visitVehicle VisitVehicle instance
   */
  remove(visitVehicle: VisitVehicle): void {
    const data: SimpleDialogData = {
      title: 'Warning!',
      message: `Do you want remove visit ${visitVehicle.visitId}?`,
      rightButtonText: 'Yes',
    };

    this.simpleDialogService.open(data).subscribe((response) => {
      if (response) {
        this.visitVehicleService.delete(visitVehicle.visitId).subscribe(() => this.loadVisitVehicles());
      }
    });
  }

  /**
   * Load visit vehicles
   */
  loadVisitVehicles(): void {
    this.visitVehicleDataSource.load(this.sort.active, this.sort.direction, this.paginator.pageSize, this.paginator.pageIndex, this.filter);

    this.visitVehicleService
      .getCount('visitId', this.sort.direction, this.paginator.pageSize, this.paginator.pageIndex, this.filter)
      .subscribe((data: any) => (this.totalVisitVehicles = data.count));
  }

  clear() {
    this.filterForm.reset();
    this.filter = {};
    this.loadVisitVehicles();
    this.paginator.pageIndex = 0;
  }

  keys(jsonObject: any) {
    return Object.keys(jsonObject);
  }

  private initTodayFilter() {
    // tslint:disable-next-line:no-string-literal
    this.filterForm.controls['todayFilter'].setValue(true);
    this.filter.today = true;
  }

  public onTodayChange(event: MatSlideToggleChange) {
    //this.filterForm.controls['dateFilter'].setValue(null);
    //this.filter.dateFilter = null;
  }

  public setExpandedElement(row: any) {
    if (this.expandedElement === row) {
      this.expandedElement = null;
    } else {
      this.expandedElement = row;

      this.movementsDataSource.load('movementId', 'desc', 5, 0, { visitId: row.visitId });
    }
  }
}
